/**
 * The Career (spec §3.2, extended by the M2 plan §6 M2-01): one immutable plain object that changes only through
 * pure command functions returning `CmdResult` (src/career/result.ts). Hot-loop rules do not apply: it is touched
 * on events, UI actions and due times, never every tick. `src/career/schema.ts` is its strict Zod mirror (saves).
 *
 * Units: credits, tonnes, game seconds (in-space sim time; the clock stops while docked).
 *
 * FROZEN after wave 1 (plan §1.2): later tasks may add optional members (and their optional schema keys) only.
 */
import type { ComponentType, ContractType, MK_COMPONENT_TYPES, ModuleId, ModuleMk, SimFactionId, SlotId, StationId, WareId } from '../data/m2Schemas'
import type { GoodId, Goods } from './parts'
import type { CorpFactionId } from '../strategic/types'
import type { IncomeMix, StoryState, TetherTotals } from '../story/types'

export type Collections = 'none' | 'notice' | 'repo'

export const LEASE_EVENT_KINDS = [
  'paid',
  'missed',
  'extra',
  'arrearsPaid',
  'lateFee',
  'interest',
  'rebuildFee',
  'repoFee',
  'premium',
  'debtIncurred',
  'debtPaid',
  'ownership',
] as const
export type LeaseEventKind = (typeof LEASE_EVENT_KINDS)[number]

/** One lease-history row (the Lease Office shows the last 20; the career keeps `lease.json historyLimit`). */
export interface LeaseEvent {
  /** Game time. */
  t: number
  kind: LeaseEventKind
  /** Credits (the payment, fee, interest or premium; 0 for `ownership`). */
  amount: number
}

export interface LeaseState {
  /** Outstanding principal; ownership when it reaches 0. */
  principal: number
  /** Interest capitalised per cycle (rises by `rateStepOnMiss` per missed payment up to `rateCap`, until ownership). */
  ratePerCycle: number
  payment: number
  cycleSeconds: number
  /** Game time of the next due payment. */
  nextDueAt: number
  /** Missed payments not yet cleared by paying the arrears. */
  missed: number
  /** Missed payments owed (credits). */
  arrears: number
  /** Late fees owed (credits). */
  feesDue: number
  collections: Collections
  /** Collections stay paused (no repo crew) until this game time (after a repo crew is defeated). */
  repoPausedUntil: number
  owned: boolean
  /** Post-ownership insurance (per-cycle premium, halves the rebuild fee). Always false before ownership. */
  insured: boolean
  history: LeaseEvent[]
}

/** What a cargo pod carries (a death-wreck pod carries the lost cargo). */
export interface CargoContents {
  /** Tonnes per ware. */
  wares: Record<WareId, number>
  components: CargoComponent[]
  /**
   * Goods (the hold's `cargo.parts` block; economy plan SY-2b, U1): a death pod carries the goods the player died
   * with, and recovering the pod returns them. Absent = none (a pod from before SY-2b loads with no migration).
   */
  goods?: Goods
  /**
   * M10 P5a0 (plan `docs/plans/2026-10-17-m10-p5a.md` Q4, review F4; P5a12d writes it): the processed tonnes of each
   * ware, each at most `wares[w]` (processing is compaction, so the tonnes stay in `wares`). Absent = none processed.
   */
  processed?: Partial<Record<WareId, number>>
}

/** A component in the hold. */
export interface CargoComponent {
  /** Unique key: the secured part's key `${wreckKey}:${partIndex}` (data cores are logged by it), or a pod id. */
  id: string
  type: ComponentType
  /** 0..1. */
  condition: number
  /**
   * Mk 1-3 of a Mk-carrying salvaged component (economy plan SY-4a/SY-4b: the secured part's rolled Mk). Absent for a
   * type without one, and in a save written before SY-4b (no migration).
   */
  mk?: number
  /**
   * M8 (MC-0a; MC-h1 sets it, MC-f5a plumbs it): a First Rush-pattern part from a derelict. Absent = not Rush. The
   * same key rides on `SalvagedModule`, so the tag survives install, uninstall and a ship switch.
   */
  provenance?: 'rush'
  /** M8 (MC-0a; MC-f5a sets it): who made the part (the study route, MC-f3). Absent = unknown (older saves). */
  maker?: MakerId
  /** Full carried mass in tonnes (a pod's includes its contents). */
  massT: number
  /** Credits at condition (`baseValue × (0.3 + 0.7 × condition)`; a pod's contents are valued separately). */
  value: number
  /** The contract this component belongs to (recovery target, courier pod). */
  contractId?: string
  /** Sealed contract cargo: never sellable. */
  sealed?: boolean
  /**
   * M7 (spec §2.8): a story item's hold mirror (sealed too; `career.story.items` is the truth), or a death-wreck pod
   * carrying story items, which skips the loot roll. Absent = not story.
   */
  story?: boolean
  /** M7 (spec §2.8, §5): contraband a patrol scan reads (a fine, never confiscation). */
  contraband?: boolean
  contents?: CargoContents
}

export interface CargoState {
  /** Tonnes (from the fitted cargo module). */
  capacityT: number
  /** Tonnes per ware. */
  wares: Record<WareId, number>
  components: CargoComponent[]
  /**
   * The goods block (economy plan SY-2a, D-TBD-c3; first the M3 relay parts, D-TBD 40): a float quantity per good
   * (every non-raw strategic ware, `career/parts.ts GOOD_IDS`), each unit weighing `cargo-parts.json massT`
   * against `capacityT`. The key keeps its pre-goods name, so older saves (the three relay parts in whole units)
   * load unchanged. Absent block or key = none.
   */
  parts?: Goods
  /**
   * M10 P5a0 (Q4, review F4; P5a12d writes it): `CargoContents.processed` on a hold: the processed tonnes of each
   * ware, each at most `wares[w]`. Absent = none processed (every hold from before P5a).
   */
  processed?: Partial<Record<WareId, number>>
}

/** M10 P5a0: the longest ship name (P5a3b's name field and `renameShip`; 1 to 24 characters, trimmed). */
export const SHIP_NAME_MAX_CHARS = 24
/** M10 P5a0: the most hull events an owned ship keeps (P5a3b drops the oldest past it). */
export const HULL_HISTORY_MAX = 16

export const HULL_EVENT_KINDS = ['bought', 'renamed', 'boarded', 'repaired', 'claimed'] as const
export type HullEventKind = (typeof HULL_EVENT_KINDS)[number]

/** M10 P5a0 (P5a3b writes it): one entry in an owned ship's history (the hangar row shows the last three). */
export interface HullEvent {
  kind: HullEventKind
  /** Faction day of the event. */
  day: number
  /** The strategic station it happened at. Absent = none recorded. */
  stationId?: string
}

/** M10 P5a0 (P5a2a writes it): the hull the player pinned as their goal in the shipyard. */
export interface PinnedHull {
  /** The hull id (`hulls.json`). */
  hull: string
  /** The strategic station whose shipyard it was pinned at. */
  stationId: string
  /** P5a2b (review F6): the goal steps already posted to the Wire, so a reload doesn't post them again. */
  posted?: string[]
}

export const PARKED_L_ORDERS = ['hold', 'escort'] as const
export type ParkedLOrder = (typeof PARKED_L_ORDERS)[number]

/** M10 P5a0 (P5a10a writes it): the player's L parked in space as a local body (plan §0.3). */
export interface ParkedL {
  /** The hangar fleet id of the parked L. */
  fleetId: string
  /** The galaxy system it is parked in. */
  systemId: string
  /** True world position, float64 metres (D-013). */
  pos: [number, number, number]
  /** Orientation quaternion `[x, y, z, w]`. */
  rot: [number, number, number, number]
  /** The captain's order (P5a10b). */
  order: ParkedLOrder
}

export interface ShipState {
  /** Ship id (`ships.json`). */
  defId: string
  hull: number
  torpedoes: number
  fitted: Record<SlotId, ModuleId>
  /** Owned, unfitted modules. */
  locker: ModuleId[]
  /**
   * Salvaged-module provenance (economy plan SY-5a, D8): modules installed from salvaged components. A fitted
   * salvaged module is in `fitted` (the truth for the fit) and in `salvaged.fitted` under the same slot; an unfitted
   * one is in `salvaged.locker` only, never in `locker`. They resell at component value, never at `sellFraction`.
   * Absent = none (saves from before SY-5a load unchanged; no migration).
   */
  salvaged?: SalvagedState
  /**
   * M7 S37: the resonance meter utility module (`modules.json utility.resonanceMeter`) is fitted on this ship (the
   * Mission 2 reward). It stays with the ship: a switch stows it in the hangar entry. Absent = not fitted.
   */
  resonanceMeter?: boolean
}

/** A component type that installs as a module (`MK_COMPONENT_TYPES`: reactor, weapon, shieldGen, thruster). */
export type MkComponentType = (typeof MK_COMPONENT_TYPES)[number]

/** A module installed from a salvaged component (economy plan SY-5a); it keeps the component's provenance. */
export interface SalvagedModule {
  /** The module it fits as: reactor → `power.mkN`, shieldGen → `shield.mkN`, thruster → `thrusters.mkN`, weapon → `pdc.mkN` or `laser.mkN`. */
  moduleId: ModuleId
  /** The source component's `CargoComponent.id` (unique across the salvaged entries). */
  sourceId: string
  type: MkComponentType
  /** 0..1: the component's condition when it was installed. */
  condition: number
  /** Matches `moduleId`'s Mk. */
  mk: ModuleMk
  /** M8 (MC-0a): the source component's `CargoComponent.provenance`, kept through install and uninstall. */
  provenance?: 'rush'
  /** M8 (MC-0a): the source component's `CargoComponent.maker`, kept through install and uninstall. */
  maker?: MakerId
}

/**
 * Salvaged-module provenance (economy plan SY-5a). A self-contained shape so M6's `OwnedShip` can carry it with the
 * ship (M6I-5, R3-H1): `locker` holds installed but unfitted salvaged modules; `fitted` the salvaged module in each
 * slot that holds one (its `moduleId` equals `ShipState.fitted[slot]`).
 */
export interface SalvagedState {
  locker: SalvagedModule[]
  fitted: Partial<Record<SlotId, SalvagedModule>>
}

/**
 * An owned fleet ship's loadout in the Career's hangar (M6 plan P21, D-TBD: the flown ship is not a worker agent).
 * The worker owns everything else about the ship (agent, order, captain, cargo, position); the Career keeps only
 * what the player's own ship state needs when they fly it, so the record stays small (50 ships within 50 KB).
 */
export interface OwnedShip {
  /** The fleet id (`ps-<seq>`, `FleetShipView.id`). Unique in the hangar. */
  fleetId: string
  /** Ship id (`ships.json`): the hull's `hulls.json shipDef`. */
  defId: string
  fitted: Record<SlotId, ModuleId>
  /** Owned, unfitted modules kept aboard this ship. */
  locker: ModuleId[]
  /** Per-ship insurance (M6 spec §4): optional once the M2 lease is owned; an uninsured loss is total. */
  insured: boolean
  /** The ship carries an escape pod (M6 spec §4 parts). */
  escapePod: boolean
  /** M7 S37: the resonance meter is fitted on this ship (`ShipState.resonanceMeter`). Absent = not fitted. */
  resonanceMeter?: boolean
  /**
   * Salvaged-module provenance travelling with the ship (economy plan M6I-5, R3-H1): SY-5a's `ShipState.salvaged`
   * shape. Leaving a ship writes the active `salvaged` here; boarding restores it. A plain `fitted`/`locker` entry is
   * never made from a salvaged one. Absent = none (hangars from before M6I-5 load unchanged).
   */
  salvaged?: SalvagedState
  /**
   * M6 (plan P24): the hull points the ship was left with by a switch (`fleet-link/switch.ts`). Absent = full (a
   * ship never flown). Boarding takes the lower of this and the worker's hp share.
   */
  hull?: number
  /** M6 (plan P24): torpedo rounds aboard when a switch left the ship. Absent = full. */
  torpedoes?: number
  /**
   * M6 (plan P24): the strategic station the Career's own lease ship (`OWN_SHIP_ID`) is parked at after a switch.
   * Fleet ships never carry it (the worker knows where they are).
   */
  stationId?: string
  /**
   * M6 (plan P24b): the Career station (`lastDockedStation`'s id space) the parked lease ship was left at, where the
   * pilot respawns after an uninsured fleet ship's total loss (`fleet-link/loss.ts`). Absent: the M2 death rule.
   */
  dockedAt?: string
  /**
   * M9 (CH-2b; CH-8 writes it): a claimed hull's flaws, a bitmask over `hulks.json`'s flaw list (the worker's
   * `FleetStore.flaws` is a Uint16, so 0..0xffff). Absent = none (every ship from before M9, and a bought ship).
   */
  flaws?: number
  /**
   * M9 (CH-2b; CH-8 writes it): the claimed hull's insured value factor (`hullValue × valueMult`), in (0, 1]. Absent
   * = 1 (pristine). CH-8b (D-572): at most `valueMultOf(flaws)` and at most `putInShare` × what the kit and yard fixes
   * put in (`hulks/insure.ts`), so it can stay below 1 on a fully fixed hull. Scrap reads the flaws, not this.
   */
  valueMult?: number
  /**
   * M10 P5a0 (P5a3b writes it): the player's name for the ship, 1 to `SHIP_NAME_MAX_CHARS` characters with no outer
   * whitespace, unique in the fleet. Absent = the worker's default name.
   */
  name?: string
  /** M10 P5a0 (P5a3b writes it): the hull's events, oldest first, at most `HULL_HISTORY_MAX`. Absent = none. */
  history?: HullEvent[]
  /**
   * M10 P5a0 (Q1, review F4; P5a8b and P5a11a write it): the hold this ship keeps while the player isn't aboard (the
   * carried S or the parked L). Absent = empty.
   */
  hold?: CargoState
  /** M10 P5a0 (P5a9b writes it): this L's cradle; `carried` is the fleet id of the S in it, or null. Absent = none. */
  cradle?: { carried: string | null }
}

export interface ContractParams {
  /** Delivery: the ware and tonnes. */
  ware?: WareId
  tons?: number
  /** Courier: the destination station. */
  destination?: StationId
  /** Recovery and clear raiders: the marker, float64 world metres. */
  marker?: [number, number, number]
  /** Recovery: the component to bring back. */
  componentType?: ComponentType
  /** Courier: the sealed pod's `CargoComponent.id`. */
  podId?: string
  /** Clear raiders: the contract tier (index into `encounters.json contractTiers`). */
  tier?: number
  // M3 bridge contract types (R22g); ids are the Reach layout's and the galaxy's.
  /** Lane repair: the lane ring to repair (`LaneRingSite.id`). */
  ringId?: string
  /** Relay parts: the offline relay (`Relay.id`). */
  relayId?: string
  /** Lane repair and relay parts: the system the target is in; chart a system: the system to chart. */
  systemId?: string
  /** Relay parts: the parts (any mix) to deliver to the relay's port. */
  units?: number
  /** Relay parts: parts delivered to that relay since the job was accepted. */
  delivered?: number
  /** Reach jobs: the target's display name, captured when the offer was generated. */
  targetName?: string
  /**
   * M8 works delivery (SNK-6b2): the good (a non-raw ware in `cargo.parts`) the works site takes. With `siteId` the
   * contract is a works hand-in: the faction treasury pays through the worker (`worksDeliver`), never `reward`.
   */
  good?: GoodId
  /** M8 works delivery (SNK-6b2): the strategic works site (`site-<seq>`) the goods go into. */
  siteId?: string
}

/** An accepted contract. `ready` = its goal is met and it can be handed in. */
export interface ContractState {
  /** `${station}:${cycle}:${k}`. */
  id: string
  type: ContractType
  /** Issuing station (standing goes to its faction). */
  station: StationId
  issuedCycle: number
  acceptedAt: number
  deadlineAt: number
  /** Credits paid on hand-in. */
  reward: number
  /** Standing gained with the issuing faction on hand-in. */
  standing: number
  /** The ×1.5 variant unlocked by standing. */
  premium?: boolean
  params: ContractParams
  status: 'active' | 'ready'
}

export interface CareerStats {
  tetherstoneT: number
  oreT: number
  partsSecured: number
  deaths: number
  /** Credits earned from sales and contract rewards. */
  earned: number
}

export interface Career {
  version: 1
  /** Career seed (uint32): seeds the space RNG, contract boards and encounters. */
  seed: number
  /** Game seconds of in-space sim time. */
  gameTime: number
  wallet: number
  /** Interest-free insurer debt after ownership. */
  debt: number
  lease: LeaseState
  cargo: CargoState
  ship: ShipState
  /** −100..100. */
  standings: Record<SimFactionId, number>
  contracts: { active: ContractState[]; completedIds: string[]; failedIds: string[] }
  /** Keys of logged data cores (M7 hook). */
  dataCores: string[]
  lastDockedStation: StationId
  stats: CareerStats
  /**
   * M5 (S-M7-1): the story tier 0..5 (M7 raises it; a dev toggle). Absent = 0, so saves from before M5 load unchanged
   * (`src/charter/careerBridge.ts careerStoryTier`); the Career sends it to the strategic worker as `setStoryTier`.
   */
  storyTier?: number
  /** M7 §12.1 logging: the last guardian ships the player destroyed (`career/logs.ts`). Absent = none (older saves). */
  guardianKills?: GuardianKillEntry[]
  /** M7 §12.1 logging: the last tetherstone sales (`career/logs.ts`). Absent = none (older saves). */
  tetherSales?: TetherSaleEntry[]
  /** M7 S1: running totals beside the bounded sale log. Absent = none logged. */
  tetherTotals?: TetherTotals
  /** M7 §12.1 (S1): rolling income by source over ~5 h of play (spec §9 retaliation rows). Absent = none logged. */
  incomeMix?: IncomeMix
  /** M7 (spec §12.1, rule 11): the story block, the truth for every story fact. Absent = no story progress yet. */
  story?: StoryState
  /**
   * M6 (plan P21): the owned fleet ships' loadouts and insurance (`src/fleet-link/hangar.ts`). Absent = none, so
   * saves from before M6 load unchanged.
   */
  hangar?: OwnedShip[]
  /**
   * M6 (plan P21): the fleet id of the hangar ship the player flies now (P24's `boardShip` sets it). Absent = the
   * Career's own (M2 lease) ship, insured through `lease.insured`.
   */
  flownShip?: string
  /**
   * M8 (MC-0a; MC-b4/b5): the pilot's berth store at each station, by Career station id. It belongs to the pilot,
   * not a ship, and death leaves it alone. Absent = nothing stored.
   */
  storage?: Record<StationId, BerthEntry>
  /** M8 (MC-0a; MC-c3a/c3b): open fabrication orders. Absent = none. */
  fabOrders?: FabOrder[]
  /**
   * M8 (MC-0a; MC-f3, MC-h5a): the one pattern-unlock store. Standing, study and blueprint fragments all write it.
   * Absent = nothing unlocked.
   */
  patterns?: PatternState
  /** M8 (MC-0a; MC-h5a): the blueprint-fragment counter (unlocks go to `patterns.unlocked`). Absent = 0. */
  blueprints?: BlueprintState
  /**
   * M8 (MC-0a; TR-1a/1b): the pilot's price log by galaxy system id: the best known price per ware and side in the
   * system, in strategic ware order. Only systems the pilot entered or covers. Absent = nothing logged.
   */
  priceLog?: Record<string, PriceLogEntry>
  /**
   * M9 (CH-2b; docs/plans/2026-10-05-m9-claimable-hulls.md §5.1): the hulks the pilot found, surveyed, is securing
   * or claiming (`sites`), and the keys of hulks that are finished (claimed and registered, or scrapped) and never
   * come back (`done`). Absent = none, so careers from before M9 load unchanged.
   */
  hulks?: HulkState
  /**
   * M10 P3b2 (spec §6.11; plan `docs/plans/2026-10-11-m10-p3.md` question 5): the hulk placement table this career's
   * galaxy was made with (`hulks.json placementTables`). Absent = 1. `newCareer` writes it only when the build's newest
   * table is above 1, so it is an integer ≥ 2 when present; `hulkTableOf` reads it. A career whose table is above the
   * build's newest is refused on load (`career/schema.ts`).
   */
  hulkTable?: number
  /**
   * M10 P4a4 (spec §4.9; `career/makerStanding.ts`): the standing each hull maker has given for purchases in the
   * current window of `makerStanding.json windowDays` faction days. Absent = nothing gained; written only by a gain.
   */
  makerStanding?: MakerStandingState
  /** M10 P5a0 (P5a2a writes it): the hull pinned as the player's goal. Absent = none pinned. */
  pinnedHull?: PinnedHull
  /** M10 P5a0 (P5a10a writes it): the player's L parked in space. Absent = none parked. */
  parkedL?: ParkedL
  /** M10 P5a0 (P5a9b writes it): the hangar fleet id of the L the flown S rides in. Absent = not carried. */
  carriedBy?: string
}

/** M10 P4a4: the purchase-standing window (`floor(day / windowDays)`) and the standing gained per maker in it. */
export interface MakerStandingState {
  window: number
  gained: Partial<Record<SimFactionId, number>>
}

// ---------------------------------------------------------------------------------------------
// M8 Materials & Craft (docs/plans/2026-10-04-m8-materials-craft.md, MC-0a): the shapes are fixed here
// ---------------------------------------------------------------------------------------------

/**
 * Who made a salvaged component: a sim faction other than the raiders, or the First Charter (derelicts; it is in
 * neither faction list). `MAKER_IDS` lists every member.
 */
export type MakerId = Exclude<SimFactionId, 'raider'> | 'first_charter'
export const MAKER_IDS = [
  'authority',
  'tethys',
  'meridian',
  'varro_security',
  'mutual',
  'holdfast',
  'first_charter',
] as const satisfies readonly MakerId[]

/** The faction patterns (MC-f1's explicit list and `patterns.json` keys). */
export const PATTERN_IDS = ['authority', 'tethys', 'meridian', 'varro', 'mutual', 'holdfast_old'] as const
export type PatternId = (typeof PATTERN_IDS)[number]
/** A pattern rung: patterns cover Mk II and Mk III only. */
export type PatternUnlockId = `${PatternId}.mk2` | `${PatternId}.mk3`
/** Every pattern-unlock id: Mk II then Mk III per pattern, in `PATTERN_IDS` order. */
export const PATTERN_UNLOCK_IDS = [
  'authority.mk2',
  'authority.mk3',
  'tethys.mk2',
  'tethys.mk3',
  'meridian.mk2',
  'meridian.mk3',
  'varro.mk2',
  'varro.mk3',
  'mutual.mk2',
  'mutual.mk3',
  'holdfast_old.mk2',
  'holdfast_old.mk3',
] as const satisfies readonly PatternUnlockId[]

/** The one unlock store (MC-f3 standing and study, MC-h5a fragments). Ids are unique. */
export interface PatternState {
  unlocked: PatternUnlockId[]
}

/** The blueprint-fragment counter (MC-h5a), in whole fragments. */
export interface BlueprintState {
  fragments: number
}

/**
 * A berth at one station (MC-b4; MC-b5's rules read `owner` and `relocateAfter`). Free storage: no rent, and it is
 * not M4 station stock.
 */
export interface BerthEntry {
  /** The station's `StationView.owner` at the first deposit. */
  owner: CorpFactionId
  goods: Goods
  /** Tonnes per raw ware; absent = none. */
  wares: Partial<Record<WareId, number>>
  components: CargoComponent[]
  /** Game time after which MC-b5's relocation rule applies. Absent = none pending. */
  relocateAfter?: number
}

/** A good or a raw ware: what a fab recipe's inputs name. */
export type GoodOrWare = GoodId | WareId
/** A fab order's carry: a ware (platinum), a good (Rush spares) or a salvage-only component type (a data core). */
export type FabCarryId = GoodOrWare | ComponentType

/**
 * A fab order, shaped like a construction site (MC-c3a/c3b). `paid` is what `startFab` charged, so `cancelFab`
 * refunds exactly that, never a re-quote.
 */
export interface FabOrder {
  /** Unique among the open orders. */
  id: string
  moduleId: ModuleId
  /** The Career station id the order was placed at. */
  stationId: StationId
  /** Game time. */
  startedAt: number
  /** Credits charged at start. */
  paid: number
  /** Inputs needed, in tonnes. */
  required: Partial<Record<GoodOrWare, number>>
  /** Inputs delivered so far, in tonnes. */
  delivered: Partial<Record<GoodOrWare, number>>
  /** The one input you can't simply buy where you fabricate (plan decision 3): tonnes or units needed and held. */
  carry: { id: FabCarryId; need: number; have: number }
  /** `CargoComponent.id`s of salvaged components given as alternative inputs (MC-c8). */
  salvageIn: string[]
}

/**
 * A system's logged prices (TR-1a): the best known buy (`b`) and sell (`s`) unit per ware in strategic ware order
 * (`strategic/wares.ts WARE_IDS`, `WARE_COUNT` long), and the station index that quoted each (`bAt`, `sAt`). Prices
 * are whole credits and station indices small ints; −1 = no data.
 */
export interface PriceLogEntry {
  /** Game time of the last record. */
  t: number
  b: number[]
  s: number[]
  bAt: number[]
  sAt: number[]
}

// ---------------------------------------------------------------------------------------------
// M9 Claimable hulls (docs/plans/2026-10-05-m9-claimable-hulls.md, CH-2b): optional, so no save version
// ---------------------------------------------------------------------------------------------

/** How a hulk's squatters were cleared (CH-6): fought off or paid (no Holdfast family scene at a hulk). */
export const HULK_CLEARED = ['fought', 'paid'] as const
export type HulkCleared = (typeof HULK_CLEARED)[number]

/** What has gone into a hulk's kit so far (CH-5b): components per type and goods in tonnes (fuel cells are a good). */
export interface HulkKit {
  components: Partial<Record<ComponentType, number>>
  goods: Goods
}

/**
 * One hulk the pilot knows (CH-2b), keyed in `HulkState.sites` by its hulk key: `hk:<system>:<n>` for a seeded
 * First Rush hulk (CH-2a) or the off-screen wreck's `ow-<seq>` (CH-3b). A site is found (CH-14a's ping) or
 * surveyed (CH-4), or both; everything after the survey is optional and absent until it happens.
 */
export interface HulkSite {
  /** The strategic hull id (`strategic/hulls.json`). */
  hull: string
  /** CH-14a: the deep-scan ping found it; it stays a nav contact. */
  found?: true
  /** CH-4: game time of the Mk II+ survey. Absent = found, not surveyed yet. */
  surveyed?: number
  /** CH-4: the survey's flaw bitmask (`rollFlaws`, by hash). Present only once surveyed. */
  flaws?: number
  /** CH-6: the coolant hazard was vented. */
  vented?: true
  /** CH-6: how the squatters were cleared. Absent = none met or not cleared yet. */
  cleared?: HulkCleared
  /** The claim is open (CH-12a `openHulkClaim`; an `ow-` record is held as claim 2 by the worker). */
  open?: true
  /** CH-5b: what has been delivered into the kit. Absent = no kit started. */
  kit?: HulkKit
  /** Game time the kit completed and the claimed ship was registered to ferry home (CH-7a). */
  claimedAt?: number
}

/**
 * The Career's hulks (CH-2b). `sites` holds only hulks still in play; a hulk that is finished moves its key to
 * `done` and drops its site, so the two never share a key and `done` keys are unique. `done` is bounded by the
 * finite seeded hulks plus one per claimed off-screen wreck.
 */
export interface HulkState {
  done: string[]
  sites: Record<string, HulkSite>
}

/** A guardian kill in the Career log: the worker's faction day, the system id and the ship's agent id. */
export interface GuardianKillEntry {
  day: number
  systemId: string
  shipId: string
  /** M7 (spec §12.1): the hull class (`ships.json` or strategic hull id). Absent on entries logged before M7. */
  hull?: string
  /** M7: the pilot's name seed (never stored as text). */
  pilotSeed?: number
  /** M7 (spec §5): the guardian hit the player first, so the kill costs no Holdfast standing. */
  selfDefence?: boolean
}

/** A tetherstone sale in the Career log: the Career clock day, the station, tonnes sold and credits received. */
export interface TetherSaleEntry {
  day: number
  stationId: string
  tonnes: number
  credits: number
  /** M7 (spec §16.9): First Rush bunker tetherstone from a restarted ore processor (logged, never extraction). */
  bunker?: boolean
}
